/**
 * Romaji ↔ Hiragana ↔ Katakana conversion.
 * Hepburn romanisation. Handles sokuon (small tsu), yoon (palatalised),
 * and sentence-final ん.
 *
 * Public API:
 *   romajiToHiragana(text)
 *   romajiToKatakana(text)
 *   hiraganaToKatakana(text)
 *   katakanaToHiragana(text)
 *   detectScript(text) -> "romaji" | "hiragana" | "katakana" | "mixed"
 *   convertAll(text) -> { romaji, hiragana, katakana, detected }
 */

// ---- Romaji → Hiragana mapping (Hepburn + common variants) ----
const R2H = {
  // vowels
  a: "あ", i: "い", u: "う", e: "え", o: "お",
  // k / g
  ka: "か", ki: "き", ku: "く", ke: "け", ko: "こ",
  ga: "が", gi: "ぎ", gu: "ぐ", ge: "げ", go: "ご",
  // s / z
  sa: "さ", shi: "し", si: "し", su: "す", se: "せ", so: "そ",
  za: "ざ", ji: "じ", zi: "じ", zu: "ず", ze: "ぜ", zo: "ぞ",
  // t / d
  ta: "た", chi: "ち", ti: "ち", tsu: "つ", tu: "つ", te: "て", to: "と",
  da: "だ", di: "ぢ", du: "づ", de: "で", do: "ど",
  // n row
  na: "な", ni: "に", nu: "ぬ", ne: "ね", no: "の",
  // h / b / p
  ha: "は", hi: "ひ", fu: "ふ", hu: "ふ", he: "へ", ho: "ほ",
  ba: "ば", bi: "び", bu: "ぶ", be: "べ", bo: "ぼ",
  pa: "ぱ", pi: "ぴ", pu: "ぷ", pe: "ぺ", po: "ぽ",
  // m / y / r / w
  ma: "ま", mi: "み", mu: "む", me: "め", mo: "も",
  ya: "や", yu: "ゆ", yo: "よ",
  ra: "ら", ri: "り", ru: "る", re: "れ", ro: "ろ",
  wa: "わ", wi: "ゐ", we: "ゑ", wo: "を",
  // yoon (palatalised)
  kya: "きゃ", kyu: "きゅ", kyo: "きょ",
  sha: "しゃ", shu: "しゅ", sho: "しょ",
  sya: "しゃ", syu: "しゅ", syo: "しょ",
  cha: "ちゃ", chu: "ちゅ", cho: "ちょ",
  tya: "ちゃ", tyu: "ちゅ", tyo: "ちょ",
  nya: "にゃ", nyu: "にゅ", nyo: "にょ",
  hya: "ひゃ", hyu: "ひゅ", hyo: "ひょ",
  mya: "みゃ", myu: "みゅ", myo: "みょ",
  rya: "りゃ", ryu: "りゅ", ryo: "りょ",
  gya: "ぎゃ", gyu: "ぎゅ", gyo: "ぎょ",
  ja:  "じゃ", ju:  "じゅ", jo:  "じょ",
  jya: "じゃ", jyu: "じゅ", jyo: "じょ",
  zya: "じゃ", zyu: "じゅ", zyo: "じょ",
  bya: "びゃ", byu: "びゅ", byo: "びょ",
  pya: "ぴゃ", pyu: "ぴゅ", pyo: "ぴょ",
  // small kana via x-prefix
  xa: "ぁ", xi: "ぃ", xu: "ぅ", xe: "ぇ", xo: "ぉ",
  xya: "ゃ", xyu: "ゅ", xyo: "ょ", xtsu: "っ", xtu: "っ",
  // common punctuation
  "-": "ー", ".": "。", ",": "、", " ": " ",
  "?": "？", "!": "！",
};

const VOWELS = new Set(["a", "i", "u", "e", "o"]);

/** Convert romaji string → hiragana. */
export function romajiToHiragana(input) {
  if (!input) return "";
  const src = input.toLowerCase();
  let out = "";
  let i = 0;
  while (i < src.length) {
    // Try longest match first (4 → 1 chars)
    let matched = false;
    for (const len of [4, 3, 2, 1]) {
      const chunk = src.slice(i, i + len);
      if (R2H[chunk]) {
        out += R2H[chunk];
        i += len;
        matched = true;
        break;
      }
    }
    if (matched) continue;

    const c = src[i];

    // Sokuon: doubled consonant (kk, ss, tt, pp, ...) → small つ
    if (
      c !== "a" && c !== "i" && c !== "u" && c !== "e" && c !== "o" &&
      c !== "n" && c === src[i + 1] && /[a-z]/.test(c)
    ) {
      out += "っ";
      i += 1;
      continue;
    }

    // Final n (not followed by vowel or y → ん)
    if (c === "n") {
      const next = src[i + 1];
      if (!next || (!VOWELS.has(next) && next !== "y" && next !== "'")) {
        out += "ん";
        i += 1;
        continue;
      }
      if (next === "'") {
        // apostrophe disambiguator like "kon'nichiwa"
        out += "ん";
        i += 2;
        continue;
      }
    }

    // Unknown char: keep as-is (numbers, foreign letters, etc.)
    out += src[i];
    i += 1;
  }
  return out;
}

/** Convert romaji string → katakana (via hiragana). */
export function romajiToKatakana(input) {
  return hiraganaToKatakana(romajiToHiragana(input));
}

/** Hiragana → Katakana (offset +0x60 in Unicode, range U+3041-U+3096). */
export function hiraganaToKatakana(input) {
  if (!input) return "";
  let out = "";
  for (const ch of input) {
    const cp = ch.codePointAt(0);
    if (cp >= 0x3041 && cp <= 0x3096) {
      out += String.fromCodePoint(cp + 0x60);
    } else {
      out += ch;
    }
  }
  return out;
}

/** Katakana → Hiragana (offset -0x60 in Unicode, range U+30A1-U+30F6). */
export function katakanaToHiragana(input) {
  if (!input) return "";
  let out = "";
  for (const ch of input) {
    const cp = ch.codePointAt(0);
    if (cp >= 0x30a1 && cp <= 0x30f6) {
      out += String.fromCodePoint(cp - 0x60);
    } else {
      out += ch;
    }
  }
  return out;
}

/** Detect dominant script. */
export function detectScript(input) {
  if (!input) return "romaji";
  let romaji = 0, hira = 0, kata = 0;
  for (const ch of input) {
    const cp = ch.codePointAt(0);
    if (cp >= 0x3041 && cp <= 0x3096) hira += 1;
    else if (cp >= 0x30a1 && cp <= 0x30f6) kata += 1;
    else if (/[a-zA-Z]/.test(ch)) romaji += 1;
  }
  if (hira === 0 && kata === 0) return "romaji";
  if (kata > hira) return "katakana";
  if (hira > kata) return "hiragana";
  return "mixed";
}

/** Convert any input into the three scripts. */
export function convertAll(input) {
  const detected = detectScript(input);
  let hiragana = "";
  let katakana = "";
  let romaji = "";
  if (detected === "romaji") {
    hiragana = romajiToHiragana(input);
    katakana = hiraganaToKatakana(hiragana);
    romaji = input.toLowerCase();
  } else if (detected === "hiragana") {
    hiragana = input;
    katakana = hiraganaToKatakana(input);
    romaji = input; // we don't reverse-romanise here (lossy + ambiguous)
  } else if (detected === "katakana") {
    katakana = input;
    hiragana = katakanaToHiragana(input);
    romaji = input;
  } else {
    hiragana = katakanaToHiragana(input); // normalise everything to hiragana
    katakana = hiraganaToKatakana(input);
    romaji = input;
  }
  return { romaji, hiragana, katakana, detected };
}
